import React, { useLayoutEffect, useRef } from 'react';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_dark from "@amcharts/amcharts4/themes/dark";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_dark);
am4core.useTheme(am4themes_animated);

export interface ISolidGaugeChartDataItem {
  category: string;
  value: number;
  maxValue: number;
  color: am4core.Color;
}

export interface ISolidGaugeChartProps {
    id: string;
    title: string;
    data: Array<ISolidGaugeChartDataItem>;
  }

const SolidGaugeChart: React.FC<ISolidGaugeChartProps> = (props: ISolidGaugeChartProps) => {
    const chart = useRef();

    useLayoutEffect(() => {
        let c = am4core.create(`${props.id}`, am4charts.RadarChart);

        chart.current = c as any;

        // Prep the chart data from the passed in props.
        let data: any[] = props.data.map(function (item) {
          return {
            category: item.category,
            value: item.value,
            maxValue: item.maxValue,
            percentComplete: Math.floor(item.value / item.maxValue * 100),
          }
        });

        c.data = data;

        let colors: am4core.Color[] = props.data.map(function(item) { return item.color });

        // Make chart not full circle
        c.startAngle = -90;
        c.endAngle = 180;
        c.innerRadius = am4core.percent(20);

        // Set number format
        //c.numberFormatter.numberFormat = "#.#'%'";

        // Create axes
        let categoryAxis = c.yAxes.push(new am4charts.CategoryAxis() as any);
        categoryAxis.dataFields.category = "category";
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.grid.template.strokeOpacity = 0;
        categoryAxis.renderer.labels.template.horizontalCenter = "right";
        categoryAxis.renderer.labels.template.fontWeight = 500;
        categoryAxis.renderer.labels.template.adapter.add("fill", function(fill : any, target : any) {
            return (target.dataItem.index >= 0) ? colors[target.dataItem.index] : fill;
        });
        categoryAxis.renderer.minGridDistance = 10;

        // Create the outside percentage numbers
        let valueAxis = c.xAxes.push(new am4charts.ValueAxis() as any);
        valueAxis.renderer.grid.template.strokeOpacity = 0;
        valueAxis.min = 0;
        valueAxis.max = "maxValue";
        valueAxis.strictMinMax = true;

        // Create the dark background series.
        let series1 = c.series.push(new am4charts.RadarColumnSeries() as any);
        series1.dataFields.valueX = "maxValue";
        series1.dataFields.categoryY = "category";
        series1.clustered = false;
        series1.columns.template.fill = new am4core.InterfaceColorSet().getFor("alternativeBackground");
        series1.columns.template.fillOpacity = 0.08;
        series1.columns.template.cornerRadiusTopLeft = 20;
        series1.columns.template.strokeWidth = 0;
        series1.columns.template.radarColumn.cornerRadius = 20;

        let series2 = c.series.push(new am4charts.RadarColumnSeries());
        series2.dataFields.valueX = "value";
        series2.dataFields.categoryY = "category";
        series2.clustered = false;
        series2.columns.template.strokeWidth = 0;
        series2.columns.template.tooltipText = "{category}: [bold]{value} out of {maxValue} charts ({percentComplete}%)";
        series2.columns.template.radarColumn.cornerRadius = 20;

        series2.columns.template.adapter.add("fill", function(fill, target : any) {
          return colors[target.dataItem.index];
        });

        // Add cursor
        c.cursor = new am4charts.RadarCursor();
    
        return () => {
          c.dispose();
        };
      }, [props]);
    
    return (
        <div className="SolidGaugeChart" id={props.id} style={{ width: "100%", height: "500px" }}></div>
    );
  }
  
  export default SolidGaugeChart;